import { useEffect, useState, useContext } from 'react'
import SectionContext from '../helpers/Context'
import createGradientElement from '../../helpers/markup'
import { lightOrDark, addBgImageClass, getSectionTheme, computeLightOrDark } from '../../helpers/style'
import { MarkupContext } from '../../providers/Markup'
import themeConfig from '../../../theme.config'
import SectionLayoutCurated from '../helpers/SectionLayoutCurated'
import SectionLayoutDefault from '../helpers/SectionLayoutDefault'

const Section = (props) => {
  const { getPaletteColor } = useContext(MarkupContext)
  // the api is sending out un-hashed hex colour strings, so we either make it the hashed version, or default to the base colour setting from the theme config.
  const [contained, setContained] = useState(false)
  const [backgroundColor, setBackgroundColor] = useState('#fff')
  const [gradientColor, setGradientColor] = useState('#fff')
  const [textShadow, setTextShadow] = useState('')

  const sectionGradient = ![null, 'None', 'Text area'].includes(props.gradientType)
  const contentGradient = ['Text area'].includes(props.gradientType)

  const titleBlockDescClass = props.titleBlockDescClass ?? ''

  useEffect(() => {
    setContained(() => props.backgroundConfiguration === 'Contained')
  }, [props.backgroundConfiguration])

  useEffect(() => {
    // we strip the '#' from the front by default because some sources of the data have it included, and some do not.
    // this sets everything to the same format, even though we add it back immediately after.
    const bgColor = props.backgroundColor?.replace('#', '')
    const fgColor = props.foregroundColor?.replace('#', '')
    setBackgroundColor((prevState) => (bgColor ? `#${bgColor}` : prevState))
    setGradientColor((prevState) => (fgColor ? `#${fgColor}` : prevState))
  }, [])

  useEffect(() => {
    setTextShadow(() => {
      if (!['always', 'images'].includes(themeConfig.settings.useTextShadow)) {
        return ''
      }
      if (
        themeConfig.settings.useTextShadow === 'images' &&
        !props.mobileBackgroundImage &&
        !props.desktopBackgroundImage
      ) {
        return ''
      }
      const theme = computeLightOrDark(backgroundColor, getPaletteColor)
      return `text-shadow shadow-${theme === 'dark' ? 'black' : 'white'}`
    })
  }, [backgroundColor, getPaletteColor])

  return (
    <section
      data-type={props.type}
      data-index={props.idx}
      data-panel-theme={lightOrDark(backgroundColor)}
      data-bg={getSectionTheme(backgroundColor)}
      style={{
        '--mobile-bg-url': props.mobileBackgroundImage ? `url(${props.mobileBackgroundImage.src})` : null,
        '--desktop-bg-url': props.desktopBackgroundImage ? `url(${props.desktopBackgroundImage.src})` : null,
        '--bg-color': backgroundColor ?? '#fff',
        '--text-color': lightOrDark(backgroundColor) === 'dark' ? 'var(--color-light)' : 'var(--color-dark)',
      }}
      className={`${props.outerClass || ''} ${contained ? 'container mx-auto' : ''} ${textShadow} ${addBgImageClass(props.desktopBackgroundImage?.src, props.mobileBackgroundImage?.src)} bg-(--bg-color) text-(--text-color)`}
    >
      <SectionContext.Provider value={{ base: backgroundColor }}>
        {sectionGradient &&
          createGradientElement({
            type: props.gradientType,
            fromColor: gradientColor,
            fullMobile: true,
          })}
        {/*
          xl:px-0 lets a full-width background bleed past the 1280 grid while the content aligns to
          it. When the background is Contained the section IS the coloured box, so removing the
          gutter left the text flush against the colour at >=1280px (NDS-362). Restore it for the
          contained case only. Deliberately not `!` — every per-component override
          (px-6! lg:px-8!, p-0!, and TestimonialCarousel's !important CSS) must still win.
        */}
        <div
          className={`inner container px-5 lg:px-8 xl:px-0 ${contained ? 'xl:px-8' : ''} mx-auto ${props.innerClass || ''} ${contentGradient ? 'has-gradient' : ''}`}
        >
          {contentGradient &&
            createGradientElement({
              type: props.gradientType,
              fromColor: gradientColor,
              fullMobile: false,
            })}
          {props.type === 'cardListCurated' ? (
            <SectionLayoutCurated sectionProps={props} titleBlockDescClass={titleBlockDescClass} />
          ) : (
            <SectionLayoutDefault sectionProps={props} titleBlockDescClass={titleBlockDescClass} />
          )}
          {props.children}
        </div>
      </SectionContext.Provider>
    </section>
  )
}

export default Section
